import React, { useCallback, useState } from 'react'

import { selectedRect } from '@tiptap/pm/tables'
import { BubbleMenu as BaseBubbleMenu } from '@tiptap/react'

import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { List } from '@tribeplatform/react-ui-kit/Layout'
import { Toggle } from '@tribeplatform/react-ui-kit/Toggle'

import { T } from '../../../../../../i18n/components/T.js'
import { getCellsInTable, hasHeaderRow } from '../../../utils.js'
import { MenuItem } from '../../ui/MenuItem.js'
import { Surface } from '../../ui/Surface.js'
import { MenuProps, ShouldShowProps } from '../types.js'

import { getColWidthForFullWidth, isRowGripSelected } from './utils.js'

export const TableRowMenu = React.memo(
  ({ editor, appendTo }: MenuProps): JSX.Element => {
    const [hasHeader, setHasHeader] = useState(false)
    const [firstRow, setFirstRow] = useState(false)
    const shouldShow = useCallback(
      ({ view, state, from }: ShouldShowProps) => {
        if (!state || !from) {
          return false
        }

        setHasHeader(hasHeaderRow(editor.view.state.selection))

        const selected = isRowGripSelected({ editor, view, state, from })

        if (!selected) {
          return selected
        }

        const rect = selectedRect(state)
        const { left, top } = rect

        if (left === 0 && top === 0) {
          setFirstRow(true)
        } else {
          setFirstRow(false)
        }

        return selected
      },
      [editor],
    )

    const onAddRowBefore = useCallback(() => {
      editor.chain().focus().addRowBefore().run()
    }, [editor])

    const onAddRowAfter = useCallback(() => {
      editor.chain().focus().addRowAfter().run()
    }, [editor])

    const onDeleteRow = useCallback(() => {
      editor.chain().focus().deleteRow().run()
    }, [editor])

    const onToggleHeaderRow = useCallback(() => {
      editor.chain().toggleHeaderRow().run()
    }, [editor])

    const onFitTableWidth = useCallback(() => {
      const colWidth = getColWidthForFullWidth(editor)
      if (!colWidth) {
        return
      }

      const allCells = getCellsInTable(editor.view.state.selection)
      if (!allCells) {
        return
      }

      const { tr } = editor.state
      allCells.forEach(cell => {
        tr.setNodeAttribute(cell.pos, 'colwidth', [colWidth])
      })

      editor.view.dispatch(tr)
    }, [editor])

    return (
      <BaseBubbleMenu
        editor={editor}
        pluginKey="tableRowMenu"
        updateDelay={0}
        tippyOptions={{
          appendTo: () => {
            return appendTo?.current
          },
          placement: 'left',
          offset: [0, 15],
          popperOptions: {
            modifiers: [{ name: 'flip', enabled: false }],
          },
        }}
        shouldShow={shouldShow}
      >
        <Surface className="text-content w-48 inline-flex h-full leading-none gap-0.5 py-6 px-2">
          <List className="w-full" direction="vertical" spacing="lg">
            {firstRow && (
              <MenuItem
                iconComponent={
                  <SvgIcon className="rotate-90" name="columns-3-active-1" />
                }
              >
                <Toggle
                  size="sm"
                  labelClassName="text-sm"
                  groupClassName="w-full items-center"
                  checked={hasHeader}
                  title={
                    <T id="Generics.HeaderRow" defaultMessage="Header Row" />
                  }
                  onChange={onToggleHeaderRow}
                />
              </MenuItem>
            )}
            <MenuItem
              iconComponent={<SvgIcon name="direction-horizontal" />}
              onClick={onFitTableWidth}
            >
              <T id="Generics.FitTableWidth" defaultMessage="Fit Table Width" />
            </MenuItem>
            {!firstRow && (
              <MenuItem
                iconComponent={<SvgIcon name="arrow-up" />}
                onClick={onAddRowBefore}
              >
                <T id="Generics.InsertAbove" defaultMessage="Insert Above" />
              </MenuItem>
            )}
            <MenuItem
              iconComponent={<SvgIcon name="arrow-down" />}
              onClick={onAddRowAfter}
            >
              <T id="Generics.InsertBelow" defaultMessage="Insert Below" />
            </MenuItem>
            <MenuItem
              iconComponent={<SvgIcon name="trash" />}
              onClick={onDeleteRow}
            >
              <T id="Generics.Delete" defaultMessage="Delete" />
            </MenuItem>
          </List>
        </Surface>
      </BaseBubbleMenu>
    )
  },
)

TableRowMenu.displayName = 'TableRowMenu'
